import { createClient, LiveTranscriptionEvents, LiveClient } from '@deepgram/sdk';
import { getCurrentDeepgramSttKey, advanceDeepgramSttKey, hasMoreDeepgramSttKeys } from './voiceApiKeyManager';

export interface TranscriptionResult {
  transcript: string;
  isFinal: boolean;
  confidence?: number;
}

export class DeepgramTranscriptionService {
  private client: LiveClient | null = null;
  private audioContext: AudioContext | null = null;
  private currentStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaRecorderChunks: Blob[] = [];
  private lastRecordingBlob: Blob | null = null;
  private isStarting: boolean = false;

  // New: persistent processors
  private processorNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;

  // Track if worklet module is registered
  private workletRegistered: boolean = false;
  private audioBufferQueue: Int16Array[] = [];

  private onTranscriptCallback: ((result: TranscriptionResult) => void) | null = null;
  private onErrorCallback: ((error: string) => void) | null = null;

  /**
   * Initialize the microphone stream and audio context without connecting to Deepgram yet.
   * This allows "warming up" the mic or keeping it active between turns.
   */
  async initializeStream(): Promise<void> {
    if (this.currentStream && this.currentStream.active) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      this.currentStream = stream;

      // Initialize AudioContext if needed
      if (!this.audioContext || this.audioContext.state === 'closed') {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
          sampleRate: 16000,
        });
        // Reset worklet registration state when creating new context
        this.workletRegistered = false;
      }

      // Register worklet module if not already registered
      if (!this.workletRegistered && this.audioContext) {
        try {
          const workletCode = `
                  class PCMProcessor extends AudioWorkletProcessor {
                      process(inputs) {
                          const input = inputs[0];
                          if (input.length > 0) {
                              const inputData = input[0];
                              this.port.postMessage(inputData);
                          }
                          return true;
                      }
                  }
                  registerProcessor('pcm-processor', PCMProcessor);
              `;
          const blob = new Blob([workletCode], { type: 'application/javascript' });
          const workletUrl = URL.createObjectURL(blob);
          await this.audioContext.audioWorklet.addModule(workletUrl);
          URL.revokeObjectURL(workletUrl); // Clean up the blob URL
          this.workletRegistered = true;
        } catch (e) {
          // Module may already be registered - mark as registered to prevent loops
          this.workletRegistered = true;
          console.warn('AudioWorklet module registration warning:', e);
        }
      }

    } catch (error) {
      console.error("Failed to initialize stream", error);
      throw error;
    }
  }

  /**
   * Start recording and transcribing audio using Deepgram Flux.
   * Will initialize stream if not already available.
   */
  async startRecording(
    onTranscript: (result: TranscriptionResult) => void,
    onError: (error: string) => void
  ): Promise<void> {
    if (this.isStarting || this.client) {
      return;
    }

    try {
      this.isStarting = true;
      this.onTranscriptCallback = onTranscript;
      this.onErrorCallback = onError;
      this.mediaRecorderChunks = [];
      this.lastRecordingBlob = null;
      this.audioBufferQueue = [];

      // Ensure stream is ready
      await this.initializeStream();
      const stream = this.currentStream!;

      // Start local recording in parallel (for "tap anywhere to send" audio blob)
      try {
        const preferredMime = MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : '';
        if (preferredMime) {
          // Re-create media recorder if needed
          this.mediaRecorder = new MediaRecorder(stream, { mimeType: preferredMime });
          this.mediaRecorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              this.mediaRecorderChunks.push(event.data);
            }
          };
          this.mediaRecorder.onerror = () => {
            // Do not fail transcription if local recording errors.
          };
          this.mediaRecorder.start(250);
        }
      } catch {
        // Best effort only.
      }


      // Create Deepgram client with current key from key manager
      const deepgram = createClient(getCurrentDeepgramSttKey());

      // Connect to Deepgram using live endpoint
      this.client = deepgram.listen.live({
        model: 'nova-3',
        language: 'en',
        smart_format: true,
        interim_results: true,
        endpointing: 1500 as any, // 1.5s pause before finalizing — prevents cutting off words mid-phrase
        utterance_end_ms: 2000 as any, // 2s silence before utterance_end event — gives users time to pause and continue
        vad_events: true as any,
        encoding: 'linear16',
        sample_rate: 16000,
        channels: 1,
      });

      // Set up event listeners
      this.client.on(LiveTranscriptionEvents.Open, () => {
        this.flushAudioBuffer();
      });

      this.client.on(LiveTranscriptionEvents.Transcript, (data: any) => {
        const transcript = data.channel?.alternatives?.[0]?.transcript;
        const isFinal = data.is_final;
        const confidence = data.channel?.alternatives?.[0]?.confidence;

        if (transcript && transcript.trim()) {
          this.onTranscriptCallback?.({
            transcript: transcript.trim(),
            isFinal: isFinal || false,
            confidence: confidence,
          });
        }
      });

      this.client.on(LiveTranscriptionEvents.Error, (error: any) => {
        this.onErrorCallback?.('Transcription error occurred');
      });

      this.client.on(LiveTranscriptionEvents.Close, () => {
      });

      // Connect nodes
      if (this.audioContext) {
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }

        // Ensure worklet is registered before creating node
        if (!this.workletRegistered) {
          try {
            const workletCode = `
                    class PCMProcessor extends AudioWorkletProcessor {
                        process(inputs) {
                            const input = inputs[0];
                            if (input.length > 0) {
                                const inputData = input[0];
                                this.port.postMessage(inputData);
                            }
                            return true;
                        }
                    }
                    registerProcessor('pcm-processor', PCMProcessor);
                `;
            const blob = new Blob([workletCode], { type: 'application/javascript' });
            const workletUrl = URL.createObjectURL(blob);
            await this.audioContext.audioWorklet.addModule(workletUrl);
            URL.revokeObjectURL(workletUrl);
            this.workletRegistered = true;
          } catch (e) {
            this.workletRegistered = true;
            console.warn('AudioWorklet module registration warning:', e);
          }
        }

        if (!this.sourceNode) {
          this.sourceNode = this.audioContext.createMediaStreamSource(stream);
        }
        if (!this.processorNode) {
          this.processorNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');
          this.processorNode.port.onmessage = (e) => {
            const inputData = e.data;
            const pcm16 = this.float32ToInt16(inputData);

            if (this.client) {
              const state = this.client.getReadyState();
              if (state === 1) { // OPEN
                this.flushAudioBuffer();
                try {
                  this.client.send(pcm16.buffer);
                } catch (err) {
                  console.error("Error sending live audio chunk to Deepgram:", err);
                }
              } else if (state === 0) { // CONNECTING
                // Queue the audio data while connecting (limit to 1000 chunks ~8 seconds)
                if (this.audioBufferQueue.length < 1000) {
                  this.audioBufferQueue.push(pcm16);
                }
              }
            }
          };
        }

        this.sourceNode.connect(this.processorNode);
        // Connect to destination to prevent garbage collection (use silent gain if needed, but usually worklet is enough)
        // Just to be safe and standard:
        const silentGain = this.audioContext.createGain();
        silentGain.gain.value = 0;
        this.processorNode.connect(silentGain);
        silentGain.connect(this.audioContext.destination);
      }

      this.isStarting = false;
    } catch (error) {
      this.isStarting = false;
      this.onErrorCallback?.('Could not start recording');
      // If we failed to start, try to cleanup connection stuff but keep stream? 
      // Actually if start fails, we might as well reset.
      this.disconnectDeepgram();
      throw error;
    }
  }

  /**
   * Stop sending data to Deepgram but keep the microphone stream active.
   */
  disconnectDeepgram(): void {
    try {
      this.isStarting = false;
      this.audioBufferQueue = [];

      // Stop Local Recorder
      if (this.mediaRecorder) {
        try {
          if (this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
          }
        } catch { }
        this.mediaRecorder = null;
      }

      // Disconnect and clean up Audio Nodes to prevent data from being sent
      if (this.processorNode) {
        try {
          // Clear the message handler to stop sending data
          this.processorNode.port.onmessage = null;
          this.processorNode.disconnect();
        } catch { }
        this.processorNode = null;
      }

      if (this.sourceNode) {
        try {
          this.sourceNode.disconnect();
        } catch { }
        this.sourceNode = null;
      }

      // Close WebSocket connection
      if (this.client) {
        try {
          this.client.finish();
        } catch { }
        this.client = null;
      }

      // Process any final chunks
      if (this.mediaRecorderChunks.length > 0) {
        try {
          this.lastRecordingBlob = new Blob(this.mediaRecorderChunks, { type: this.mediaRecorderChunks[0]?.type || 'audio/webm' });
        } catch {
          this.lastRecordingBlob = null;
        }
      }
    } catch (error) {
      console.error('Error disconnecting Deepgram:', error);
    }
  }

  /**
 * Completely clean up: stop stream, close context, etc.
 * Call this when Voice Mode closes.
 */
  dispose(): void {
    this.disconnectDeepgram();
    this.audioBufferQueue = [];

    if (this.currentStream) {
      this.currentStream.getTracks().forEach(track => track.stop());
      this.currentStream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.sourceNode = null;
    this.processorNode = null;
    this.workletRegistered = false;
  }

  // Alias for backward compatibility if needed, but better to use specific methods
  stopRecording(): void {
    this.disconnectDeepgram();
  }

  /**
   * Stop recording and return the most recent captured audio blob (best effort).
   * Note: transcript events may still arrive briefly after stopping.
   */
  async stopRecordingAndGetBlob(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const recorder = this.mediaRecorder;
      if (!recorder || recorder.state === 'inactive') {
        this.disconnectDeepgram();
        resolve(this.lastRecordingBlob);
        return;
      }

      const finalize = () => {
        try {
          if (this.mediaRecorderChunks.length > 0) {
            this.lastRecordingBlob = new Blob(this.mediaRecorderChunks, { type: this.mediaRecorderChunks[0]?.type || 'audio/webm' });
          }
        } catch {
          this.lastRecordingBlob = null;
        }
        resolve(this.lastRecordingBlob);
      };

      try {
        recorder.addEventListener('stop', finalize, { once: true } as any);
        this.disconnectDeepgram();
      } catch {
        this.disconnectDeepgram();
        finalize();
      }
    });
  }

  /**
   * Flush any buffered audio chunks to Deepgram once the connection is open.
   */
  private flushAudioBuffer(): void {
    if (this.client && this.client.getReadyState() === 1 && this.audioBufferQueue.length > 0) {
      for (const chunk of this.audioBufferQueue) {
        try {
          this.client.send(chunk.buffer);
        } catch (err) {
          console.error("Error sending buffered audio chunk to Deepgram:", err);
        }
      }
      this.audioBufferQueue = [];
    }
  }

  /**
   * Convert Float32Array to Int16Array (Linear16 PCM)
   */
  private float32ToInt16(float32Array: Float32Array): Int16Array {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array;
  }

  /**
   * Check if currently connected to Deepgram
   */
  isRecording(): boolean {
    return this.client !== null;
  }
}

// Export singleton instance
export const deepgramService = new DeepgramTranscriptionService();
